/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Redirect } from 'react-router';

import { DataProxy, FetchResult, useMutation, useQuery } from '@apollo/client';
import { Box, Flex, Loader } from '@opendoor/bricks/core';

import {
  IUpdatePartnerBranchMember,
  IUpdatePartnerBranchMemberVariables,
  UPDATE_PARTNER_BRANCH_MEMBER,
} from '../graphql/mutations/UpdatePartnerBranchMember';
import {
  GET_PARTNER_BRANCHES,
  IGetPartnerBranches,
  IPartnerBranchData,
} from '../graphql/queries/GetPartnerBranches';
import { formatPhoneNumberInput, sortRegionsByDisplayName } from '../helpers/formatUtils';
import { ERROR_PAGE_ROUTE } from '../routes/routes';
import ContactsSection from './ContactsSection';
import { DropdownSelect, OptionType } from './DropdownSelects';

const ContactsPanel = () => {
  const updateCache = (cache: DataProxy, { data }: FetchResult<IUpdatePartnerBranchMember>) => {
    const partnerBranchesCache: IGetPartnerBranches | null = cache.readQuery({
      query: GET_PARTNER_BRANCHES,
    });

    if (partnerBranchesCache && data?.updatePartnerBranchMember) {
      cache.writeQuery({
        query: GET_PARTNER_BRANCHES,
        data: {
          partnerBranches: partnerBranchesCache.partnerBranches,
          currentPartnerBranch: data.updatePartnerBranchMember,
        },
      });
    }
  };

  const [updateMutation, { loading: mutationLoading, error: mutationError }] = useMutation<
    IUpdatePartnerBranchMember,
    IUpdatePartnerBranchMemberVariables
  >(UPDATE_PARTNER_BRANCH_MEMBER, { update: updateCache });

  const getCurrentValue = (currentBranch: IPartnerBranchData | null): OptionType => {
    if (currentBranch) {
      return {
        value: currentBranch.branchId,
        label: currentBranch.marketDisplayName,
      };
    }

    return {
      value: 'none',
      label: 'Select Your Market',
    };
  };

  const renderContactsSection = (currentBranch: IPartnerBranchData | null) => {
    if (currentBranch && currentBranch.partnerManagerContact) {
      return (
        <ContactsSection
          contacts={[
            {
              email: currentBranch.partnerManagerContact.email,
              name: currentBranch.partnerManagerContact.fullName,
              phone: currentBranch.partnerManagerContact.phoneNumber
                ? formatPhoneNumberInput(currentBranch.partnerManagerContact.phoneNumber)
                : '',
            },
          ]}
          title="Your Opendoor Contact"
        />
      );
    }
  };

  const {
    loading: queryLoading,
    error: queryError,
    data,
  } = useQuery<IGetPartnerBranches>(GET_PARTNER_BRANCHES);
  if (queryLoading) {
    return <Loader />;
  }

  if (queryError) {
    return <Redirect to={ERROR_PAGE_ROUTE} />;
  }

  const handleDropdownChange = (newValue: OptionType | undefined) => {
    if (
      newValue &&
      (!data!.currentPartnerBranch ||
        (data!.currentPartnerBranch && newValue.value !== data!.currentPartnerBranch!.branchId))
    ) {
      updateMutation({ variables: { branchID: newValue.value } });
    }
  };

  const options = data!.partnerBranches
    .filter((branch) => {
      return branch.marketActive;
    })
    .map((branch) => ({
      value: branch.branchId,
      label: branch.marketDisplayName,
    }))
    .sort(sortRegionsByDisplayName);

  return (
    <Flex flexDirection="column">
      <DropdownSelect
        options={options}
        value={getCurrentValue(data!.currentPartnerBranch)}
        onChange={handleDropdownChange}
      />
      <Box mt={5}>
        {mutationLoading ? <Loader /> : renderContactsSection(data!.currentPartnerBranch)}
        {mutationError && <Redirect to={ERROR_PAGE_ROUTE} />}
      </Box>
    </Flex>
  );
};

export default ContactsPanel;
